import type { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import type { ChatMessage } from "../../interfaces";
import type { ScheduledJobProto } from "build-ts/schedule";

// ─── Text transforms ──────────────────────────────────────────────────────────

/** Transform [[dataframe: table_name]] → markdown link */
export const transformDataframeLinks = (text: string): string =>
	/**
	 * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
	 * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
	 */
	text.replace(/\[\[dataframe:\s*([^\]]+)\]\]/gi, (_, tableName) => {
		const cleanName = tableName.trim();
		return `[${cleanName}](#dataframe:${encodeURIComponent(cleanName)})`;
	});

/** Transform [[workflow: workflow_id]] → markdown link */
export const transformAnalysisLinks = (text: string): string =>
	/**
	 * IDs like spain-ads-cpl-outliers-(spend->-100) and those with &, +, > can
	 * break/truncate markdown link parsing when inserted raw so doing encodeURIComponent
	 */
	text.replace(/\[\[workflow:\s*([^\]]+)\]\]/gi, (_, llmId) => {
		const cleanId = llmId.trim();
		return `[${cleanId}](#workflow:${encodeURIComponent(cleanId)})`;
	});

/** Transform [[chart: chart_name]] → markdown link */
export const transformChartLinks = (text: string): string =>
	text.replace(/\[\[chart:\s*([^\]]+)\]\]/gi, (_, chartName) => {
		const cleanName = chartName.trim();
		return `[${cleanName}](#chart:${encodeURIComponent(cleanName)})`;
	});

/** Transform [[code: snippet_name]] → markdown link that opens the artifact on the code tab */
export const transformCodeLinks = (text: string): string =>
	text.replace(/\[\[code:\s*([^\]]+)\]\]/gi, (_, snippetName) => {
		const cleanName = snippetName.trim();
		return `[${cleanName}](#code:${encodeURIComponent(cleanName)})`;
	});

/** Transform [[report:name]] / [[docx:name]] → markdown link that opens the report artifact */
export const transformReportLinks = (text: string): string =>
	text.replace(/\[\[(?:report|docx):\s*([^\]]+)\]\]/gi, (_, title) => {
		const cleanTitle = title.trim();
		return `[${cleanTitle}](#report:${encodeURIComponent(cleanTitle)})`;
	});

/**
 * Escapes currency-style dollar signs ($36,756) so remark-math doesn't treat
 * them as LaTeX delimiters. Math expressions always start with \, a letter,
 * or a space after $, never directly with a digit.
 */
export const escapeCurrencyDollar = (text: string): string =>
	text.replace(/\$(?=\d)/g, "\\$");

/**
 * Normalizes LaTeX delimiters in markdown text.
 * Converts \[ and \] to $$ and \( and \) to $.
 */
export function normalizeLatex(markdown: string): string {
	return markdown.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$").replace(/\\\(/g, "$").replace(/\\\)/g, "$");
}

// ─── Artifact lookup helpers ──────────────────────────────────────────────────

/** Plain JSON from SSE uses string enum names and snake_case; persisted payloads may be normalized by fromJSON. */
export function getKgDataframePayload(artifact: ArtifactProto | Record<string, unknown>) {
	const a = artifact as Record<string, unknown>;
	return (a.kg_dataframe ?? a.kgDataframe) as { header?: { id?: string; name?: string } } | undefined;
}

export type ArtifactLookupOpts = {
	executionSteps?: ExecutionStepProto[];
	messageArtifacts?: ArtifactProto[];
	chatMessages?: ChatMessage[];
};

/**
 * Core traversal shared by all fallback find functions.
 * Walks executionSteps (newest-first) → messageArtifacts → chatMessages (newest-first).
 * The input name is assumed to already be trimmed (done in the transform step).
 * The .trim() on stored values guards against raw SSE data with unclean names.
 */
function findArtifactInMessageChain(
	matches: (a: ArtifactProto) => boolean,
	opts: ArtifactLookupOpts,
): ArtifactProto | undefined {
	const steps = opts.executionSteps;
	if (steps?.length) {
		for (let i = steps.length - 1; i >= 0; i--) {
			for (const a of steps[i].artifacts) {
				if (matches(a as ArtifactProto)) return a as ArtifactProto;
			}
		}
	}

	const fromList = (list: ArtifactProto[] | undefined) =>
		list?.find(x => matches(x));

	const found = fromList(opts.messageArtifacts);
	if (found) return found;

	const msgs = opts.chatMessages;
	for (let i = (msgs?.length ?? 0) - 1; i >= 0; i--) {
		const msg = msgs![i];
		const fromMsg = fromList(msg.artifacts);
		if (fromMsg) return fromMsg;
		const st = msg.executionSteps;
		if (st?.length) {
			for (let j = st.length - 1; j >= 0; j--) {
				for (const a of st[j].artifacts) {
					if (matches(a as ArtifactProto)) return a as ArtifactProto;
				}
			}
		}
	}
	return undefined;
}

/** Resolve [[dataframe:name]] to an artifact: checks KG_DATAFRAME by header name and DATAFRAME_ANALYSIS by pandas_dataframe names. */
export function findKGDataframeArtifactByTableName(
	tableName: string,
	opts: ArtifactLookupOpts,
): ArtifactProto | undefined {
	return findArtifactInMessageChain(artifact => {
		const type = artifactTypeFromJSON(artifact.artifact_type);
		if (type === ArtifactType.KG_DATAFRAME) {
			return getKgDataframePayload(artifact)?.header?.name?.trim() === tableName;
		}
		if (type === ArtifactType.DATAFRAME_ANALYSIS) {
			return (artifact.dataframe_analysis?.pandas_dataframes ?? []).some(f => f.name?.trim() === tableName);
		}
		return false;
	}, opts);
}

/** Resolve [[chart:name]] to its parent DATAFRAME_ANALYSIS artifact. */
export function findChartArtifactByName(
	chartName: string,
	opts: ArtifactLookupOpts,
): ArtifactProto | undefined {
	return findArtifactInMessageChain(
		artifact =>
			artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS &&
			(artifact.dataframe_analysis?.charts ?? []).some(c => c.name?.trim() === chartName),
		opts,
	);
}

/** Resolve [[code:name]] to its DATAFRAME_ANALYSIS artifact (code tab). */
export function findCodeArtifactByName(
	artifactName: string,
	opts: ArtifactLookupOpts,
): ArtifactProto | undefined {
	return findArtifactInMessageChain(
		artifact =>
			artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS &&
			artifact.dataframe_analysis?.header?.name?.trim() === artifactName,
		opts,
	);
}

/**
 * Resolve [[report:name]] / [[docx:name]] to a DATAFRAME_ANALYSIS artifact that
 * has a matching reports[].name entry.
 */
export function findReportArtifactByName(
	reportName: string,
	opts: ArtifactLookupOpts,
): ArtifactProto | undefined {
	return findArtifactInMessageChain(
		artifact =>
			artifactTypeFromJSON(artifact.artifact_type) === ArtifactType.DATAFRAME_ANALYSIS &&
			(artifact.dataframe_analysis?.reports ?? []).some(r => r.name?.trim() === reportName),
		opts,
	);
}

export type WorkflowLookupOpts = ArtifactLookupOpts & {
	scenarioJobs?: ScheduledJobProto[];
};

/** Resolve [[workflow:darwinName]] to a SCENARIO_JOB artifact. */
export function findWorkflowArtifactByDarwinName(
	darwinName: string,
	opts: WorkflowLookupOpts,
): ArtifactProto | undefined {
	const isMatch = (a: ArtifactProto) =>
		artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB &&
		(a.scenario_job?.scheduled_analysis as { darwin_name?: string } | undefined)?.darwin_name === darwinName;

	const found = findArtifactInMessageChain(isMatch, opts);
	if (found) return found;

	// scenarioJobs are tracked separately (not in message artifacts) — check them last.
	const job = opts.scenarioJobs?.find(
		j => (j.scheduled_analysis as { darwin_name?: string } | undefined)?.darwin_name === darwinName,
	);
	if (job) return { artifact_type: ArtifactType.SCENARIO_JOB, scenario_job: job };
	return undefined;
}

export function getChartIndexInArtifact(artifact: ArtifactProto, chartName: string): number {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
	return (artifact.dataframe_analysis?.charts ?? []).findIndex(c => c.name?.trim() === chartName);
}

export function getDataframeIndexInArtifact(artifact: ArtifactProto, dataframeName: string): number {
	if (artifactTypeFromJSON(artifact.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) return -1;
	return (artifact.dataframe_analysis?.pandas_dataframes ?? []).findIndex(df => df.name?.trim() === dataframeName);
}
